import {
  ApInvoiceTransactionStatusEnum,
  useUpdateTransactionItem,
} from '@/context/services/transaction_items/List/TransactionItems.service';
import { TransactionItemFormType } from '@/features/transaction_items/List/type';
import { NUMERIC_TYPE_SUPPORTED_DECIMAL_PLACES } from '@/features/transaction_items/constants';
import { useErrorTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { useTranslation } from '@/i18n';
import { useCallback } from 'react';
import { SubmitHandler, UseFormSetError } from 'react-hook-form';
import { useParams } from 'react-router-dom';

type Props = {
  transaction_item_id: string;
  setValidationError: UseFormSetError<TransactionItemFormType>;
  handlerSuccess: (successMsg: string) => Promise<void>;
  handlerError: (errorMsg: string) => void;
  showLoading: (loading: boolean) => void;
};

const toOptionalString = (
  value: string | number | null | undefined
): string | undefined => {
  if (value === null || value === undefined) return undefined;
  return value.toString();
};

export const useSubmitUpdateTransactionItem = ({
  transaction_item_id,
  setValidationError,
  handlerSuccess,
  handlerError,
  showLoading,
}: Props) => {
  const { report_type_id } = useParams();
  const { mutateAsync } = useUpdateTransactionItem(report_type_id ?? '');
  const { t } = useTranslation();
  const { send } = useErrorTrackingSystem();

  const onSubmitUpdate: SubmitHandler<TransactionItemFormType> = useCallback(
    async (form) => {
      try {
        showLoading(true);
        const resp = await mutateAsync({
          id: transaction_item_id,
          data: {
            ap_invoice_transaction_general_input: {
              label: form.label,
              is_visible: form.is_visible,
              placeholder: form.placeholder,
              date_format_type: form.date_format_type,
              tooltip_message: form.tooltip_message,
              is_required: false,
              min_value: toOptionalString(form.min_value),
              max_value: toOptionalString(form.max_value),
              min_chars: toOptionalString(form.min_chars),
              max_chars: toOptionalString(form.max_chars),
              decimal_places: form.allow_decimal
                ? NUMERIC_TYPE_SUPPORTED_DECIMAL_PLACES
                : 0,
            },
          },
        });
        if (resp.status === ApInvoiceTransactionStatusEnum.SUCCESS) {
          await handlerSuccess(t('transaction_items.update.success'));
        } else if (
          resp.status === ApInvoiceTransactionStatusEnum.INVALID_ERROR
        ) {
          Object.entries(resp.errors.errors).forEach(([key, value]) => {
            if (key in form) {
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              setValidationError(key as keyof TransactionItemFormType, {
                type: 'validate',
                message:
                  value.type === 'taken'
                    ? t('transaction_items.validation_message.taken')
                    : value.message,
              });
            } else {
              handlerError(t('transaction_items.update.error'));
            }
          });
        } else if (resp.status === ApInvoiceTransactionStatusEnum.AUTH_ERROR) {
          location.href = resp.redirectPath;
        } else {
          handlerError(t('transaction_items.update.error'));
        }
      } catch (error) {
        if (error instanceof Error && error?.name === 'AxiosError') {
          send(error.message);
        } else {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          send(error as Error);
        }
        handlerError(t('transaction_items.update.error'));
      } finally {
        showLoading(false);
      }
    },
    [
      showLoading,
      mutateAsync,
      transaction_item_id,
      handlerSuccess,
      t,
      setValidationError,
      handlerError,
      send,
    ]
  );

  return { onSubmitUpdate };
};
